import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import { useGetAuditLogs } from "./useGetAuditLogs";
import Sidebar from "../../components/ui/Sidebar";

import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import MetricsWidget from "./components/MetricsWidget";
import FilterPanel from "./components/FilterPanel";
import AuditTable from "./components/AuditTable";
import ExportModal from "./components/ExportModal";

const AuditLogsContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    user: "",
    actionType: "",
    status: "",
    search: "",
  });
  const [sortConfig, setSortConfig] = useState({
    field: "timestamp",
    direction: "desc",
  });
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, error } = useGetAuditLogs({
    page: currentPage,
    limit: 10,
  });

  const logs = data?.auditLogs || [];
  const pagination = data?.pagination || {};

  // Mock audit logs data
  const auditLogs = logs || [];

  // Mock metrics data
  const metricsData = [
    {
      title: t("auditLogs.metrics.totalActivities"),
      value: "2,847",
      subtitle: t("auditLogs.metrics.last30Days"),
      icon: "Activity",
      trend: "up",
      trendValue: "+12.5%",
      status: "default",
    },
    {
      title: t("auditLogs.metrics.failedActions"),
      value: "23",
      subtitle: t("auditLogs.metrics.requiresAttention"),
      icon: "AlertTriangle",
      trend: "down",
      trendValue: "-8.2%",
      status: "warning",
    },
    {
      title: t("auditLogs.metrics.uniqueUsers"),
      value: "47",
      subtitle: "Active this month",
      icon: "Users",
      trend: "up",
      trendValue: "+3 new",
      status: "success",
    },
    {
      title: t("auditLogs.metrics.complianceEvents"),
      value: "156",
      subtitle: t("auditLogs.metrics.zatcaSubmissions"),
      icon: "Shield",
      trend: "up",
      trendValue: "+18.7%",
      status: "default",
    },
  ];

  // Filter and search logic
  useEffect(() => {
    let filtered = [...auditLogs];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(
        (log) =>
          log?.user?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          log?.resource?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          log?.details?.description
            ?.toLowerCase()
            ?.includes(searchQuery?.toLowerCase()),
      );
    }

    // Apply filters
    if (filters?.dateFrom) {
      filtered = filtered?.filter(
        (log) => new Date(log.timestamp) >= new Date(filters.dateFrom),
      );
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(
        (log) => new Date(log.timestamp) <= new Date(filters.dateTo),
      );
    }
    if (filters?.user) {
      filtered = filtered?.filter((log) =>
        log?.user?.toLowerCase()?.includes(filters?.user?.toLowerCase()),
      );
    }
    if (filters?.actionType) {
      filtered = filtered?.filter(
        (log) => log?.actionType === filters?.actionType,
      );
    }
    if (filters?.status) {
      filtered = filtered?.filter((log) => log?.status === filters?.status);
    }
    if (filters?.search) {
      filtered = filtered?.filter(
        (log) =>
          log?.user?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
          log?.resource
            ?.toLowerCase()
            ?.includes(filters?.search?.toLowerCase()) ||
          log?.details?.description
            ?.toLowerCase()
            ?.includes(filters?.search?.toLowerCase()),
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.field];
      let bValue = b?.[sortConfig?.field];

      if (sortConfig?.field === "timestamp") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredLogs(filtered);
  }, [searchQuery, filters, sortConfig, auditLogs]);

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev?.field === field && prev?.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {};

  const handleClearFilters = () => {
    const clearedFilters = {
      dateFrom: "",
      dateTo: "",
      user: "",
      actionType: "",
      status: "",
      search: "",
    };
    setFilters(clearedFilters);
    setSearchQuery("");
  };

  const handleExport = (exportConfig) => {
    // Mock export functionality
    const exportData = {
      logs: filteredLogs,
      config: exportConfig,
      timestamp: new Date()?.toISOString(),
    };

    // Simulate file download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${new Date()?.toISOString()?.split("T")?.[0]}.${
      exportConfig?.format
    }`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {t("auditLogs.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("auditLogs.description")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setIsFilterPanelOpen(true)}
                iconName="Filter"
                iconPosition="left"
                className="lg:hidden"
              >
                {t("actions.filters")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsExportModalOpen(true)}
                iconName="Download"
                iconPosition="left"
              >
                {t("actions.export")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8"
        >
          {metricsData?.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
            >
              <MetricsWidget
                title={metric?.title}
                value={metric?.value}
                subtitle={metric?.subtitle}
                icon={metric?.icon}
                trend={metric?.trend}
                trendValue={metric?.trendValue}
                status={metric?.status}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1 space-y-6">
            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-gray-100 dark:border-gray-700"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    type="search"
                    placeholder={t("auditLogs.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {t("common.showingRange", {
                      from: filteredLogs?.length ? 1 : 0,
                      to: filteredLogs?.length,
                      total: auditLogs?.length,
                      entity: t("entities.logs"),
                    })}
                  </span>
                  {(searchQuery || Object.values(filters)?.some((f) => f)) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      iconName="X"
                      iconPosition="left"
                    >
                      {t("actions.clear")}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Audit Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
            >
              <AuditTable
                logs={filteredLogs}
                onSort={handleSort}
                sortConfig={sortConfig}
                onExport={() => setIsExportModalOpen(true)}
                pagination={pagination}
                onPageChange={setCurrentPage}
              />
            </motion.div>
          </div>

          {/* Desktop Filter Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="hidden lg:block w-80"
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700">
              <FilterPanel
                isOpen={true}
                onClose={() => {}}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Mobile Filter Panel */}
      <AnimatePresence>
        {isFilterPanelOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FilterPanel
              isOpen={isFilterPanelOpen}
              onClose={() => setIsFilterPanelOpen(false)}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Export Modal */}
      <AnimatePresence>
        {isExportModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ExportModal
              isOpen={isExportModalOpen}
              onClose={() => setIsExportModalOpen(false)}
              onExport={handleExport}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AuditLogs = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <AuditLogsContent />
      </div>
    </SidebarProvider>
  );
};

export default AuditLogs;
